import axios from 'axios';

// Get API URL from environment variables
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Store guest token in sessionStorage
const GUEST_TOKEN_KEY = 'quiz_guest_token';

// Get guest token from URL if present and store it
const getGuestToken = () => {
  // First check URL for new token
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get('t');
  
  if (urlToken) {
    // Store in sessionStorage for persistence
    sessionStorage.setItem(GUEST_TOKEN_KEY, urlToken);
    
    // Clean up URL to remove token parameter
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('t');
    window.history.replaceState({}, document.title, newUrl.toString());
    
    return urlToken;
  }
  
  // If no URL token, check sessionStorage
  const storedToken = sessionStorage.getItem(GUEST_TOKEN_KEY);
  
  return storedToken;
};

// Clear guest token
export const clearGuestToken = () => {
  sessionStorage.removeItem(GUEST_TOKEN_KEY);
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for cookies/sessions
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor for guest token
api.interceptors.request.use(
  (config) => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    }
    
    // Add guest token to headers if present
    const guestToken = getGuestToken();
    if (guestToken) {
      config.headers = config.headers || {};
      config.headers['X-Quiz-Token'] = guestToken;
    }
    
    return config;
  },
  (error) => {
    // Only log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Request Error:', error);
    }
    return Promise.reject(error);
  }
);

// Session expiration handler (will be set by AuthContext)
let sessionExpiredHandler: ((reason: string) => void) | null = null;

export const setSessionExpiredHandler = (handler: (reason: string) => void) => {
  sessionExpiredHandler = handler;
};

// Flag to track intentional logout (prevents session expired handler during logout)
let isLoggingOut = false;

export const setIsLoggingOut = (value: boolean) => {
  isLoggingOut = value;
};

export const getIsLoggingOut = () => isLoggingOut;

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  (error) => {
    // Only log errors in development or for critical errors
    if (process.env.NODE_ENV === 'development' || error.response?.status >= 500) {
      console.error('API Response Error:', error.response || error);
    }
    
    const status = error.response?.status;
    const url = error.config?.url || '';
    
    // Handle authentication errors
    if (status === 401 || status === 403) {
      // Skip auth check endpoints to avoid infinite loops
      const isAuthEndpoint = url.includes('/api/auth/status') || 
                             url.includes('/api/auth/logout') ||
                             url.includes('/api/auth/google') ||
                             url.includes('/api/auth/microsoft');
      
      // Skip session expired handler if user is intentionally logging out
      if (!isAuthEndpoint && !isLoggingOut) {
        console.warn('🔒 Session expired or unauthorized:', { status, url });
        
        // If we have a guest token that failed, clear it
        if (getGuestToken()) {
          clearGuestToken();
        }
        
        // Call the session expired handler to force logout
        if (sessionExpiredHandler) {
          const reason = status === 401 ? 'session_expired' : 'unauthorized';
          sessionExpiredHandler(reason);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  // Check authentication status
  checkStatus: () => api.get('/api/auth/status'),
  
  // Logout user
  logout: () => {
    // Clear guest token on logout
    clearGuestToken();
    return api.post('/api/auth/logout');
  },
  
  // Get login URL (for redirecting to Google OAuth)
  getGoogleLoginURL: () => `${API_URL}/api/auth/google`,
  getMicrosoftLoginURL: () => `${API_URL}/api/auth/microsoft`,
  
  // Development login (only available in development)
  // devLogin: (email: string, name: string, role: string = 'admin') => 
  //   api.post('/api/auth/dev-login', { email, name, role }),
  
  // Get active sessions (admin only)
  getActiveSessions: () => api.get('/api/auth/active-sessions'),
  
  // Get session activity (admin only)
  getSessionActivity: (sessionId: string) => 
    api.get(`/api/auth/session/${sessionId}/activity`),
  
  // Get session statistics (admin only)
  getSessionStats: () => api.get('/api/auth/session-stats'),
  
  // Revoke a session (admin only)
  revokeSession: (sessionId: string) => 
    api.delete(`/api/auth/session/${sessionId}`),
  
  // Get pending messages for current session
  getMessages: (lastCheckTime?: Date) => 
    api.get('/api/auth/messages', { 
      params: lastCheckTime ? { lastCheckTime: lastCheckTime.toISOString() } : {} 
    }),
  
  // Acknowledge a message
  acknowledgeMessage: (messageId: string) => 
    api.post(`/api/auth/messages/${messageId}/acknowledge`),
  
  // Send message to session (admin only)
  sendMessage: (data: {
    sessionId?: string;
    message: string;
    type?: 'info' | 'warning' | 'error' | 'success' | 'announcement';
    title?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    expiresInMinutes?: number;
    metadata?: Record<string, any>;
  }) => api.post('/api/auth/messages/send', data),
  
  // Get all messages (admin only)
  getAllMessages: () => api.get('/api/auth/messages/all'),
  
  // Delete a message (admin only)
  deleteMessage: (messageId: string) => 
    api.delete(`/api/auth/messages/${messageId}`),
  
  // Get notifications for current user
  getNotifications: (params?: {
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
    category?: string;
  }) => api.get('/api/auth/notifications', { params }),
  
  // Get unread notification count
  getUnreadCount: () => api.get('/api/auth/notifications/unread-count'),
  
  // Mark notification as read
  markNotificationAsRead: (notificationId: number) => 
    api.post(`/api/auth/notifications/${notificationId}/read`),
  
  // Mark all notifications as read
  markAllNotificationsAsRead: () => 
    api.post('/api/auth/notifications/read-all'),
  
  // Delete a notification (admin only)
  deleteNotification: (notificationId: number) => 
    api.delete(`/api/auth/notifications/${notificationId}`)
};

export default api; 

// Question Pools API
export const questionPoolsAPI = {
  list: (quizId: number | string) => api.get(`/api/quizzes/${quizId}/question-pools`),
  getActive: (quizId: number | string) => api.get(`/api/quizzes/${quizId}/question-pools/active`),
  create: (
    quizId: number | string,
    data: { name: string; description?: string; max_questions: number; is_active?: boolean }
  ) => api.post(`/api/quizzes/${quizId}/question-pools`, data),
  update: (
    quizId: number | string,
    poolId: number | string,
    data: Partial<{ name: string; description?: string; max_questions: number; is_active: boolean }>
  ) => api.put(`/api/quizzes/${quizId}/question-pools/${poolId}`, data),
  remove: (quizId: number | string, poolId: number | string) =>
    api.delete(`/api/quizzes/${quizId}/question-pools/${poolId}`),
  activate: (quizId: number | string, poolId: number | string) =>
    api.post(`/api/quizzes/${quizId}/question-pools/${poolId}/activate`, {}),
  randomize: (quizId: number | string, poolId: number | string) =>
    api.post(`/api/quizzes/${quizId}/question-pools/${poolId}/randomize`, {}),
};

// Question activation API
export const questionActivationAPI = {
  toggle: (questionId: number | string) => api.put(`/api/quizzes/questions/${questionId}/toggle-active`, {}),
  set: (questionId: number | string, isActive: boolean) =>
    api.put(`/api/quizzes/questions/${questionId}/set-active`, { isActive }),
};

// Reporting API
export const reportingAPI = {
  // Quiz-specific reports
  getQuizSummary: (quizId: number | string, params?: any) => 
    api.get(`/api/reports/quiz/${quizId}/summary`, { params }),
  
  getQuizDetailed: (quizId: number | string, params?: any) => 
    api.get(`/api/reports/quiz/${quizId}/detailed`, { params }),
  
  getQuizParticipants: (quizId: number | string, params?: any) => 
    api.get(`/api/reports/quiz/${quizId}/participants`, { params }),
  
  getQuizTrends: (quizId: number | string, params?: any) => 
    api.get(`/api/reports/quiz/${quizId}/trends`, { params }),

  // System-wide reports
  getSystemOverview: (params?: any) => 
    api.get('/api/reports/system/overview', { params }),
  
  getCategoryPerformance: (params?: any) => 
    api.get('/api/reports/system/categories', { params }),
  
  getSystemTrends: (params?: any) => 
    api.get('/api/reports/system/trends', { params }),

  getOverallQuestionPerformance: (params?: any) => 
    api.get('/api/reports/system/question-performance', { params }),

  getQuizQuestionPerformance: (quizId: number | string, params?: any) => 
    api.get(`/api/reports/quiz/${quizId}/question-performance`, { params }),

  // Comparison and utility
  compareQuizzes: (quizIds: number[], params?: any) => 
    api.post('/api/reports/quiz/compare', { quizIds }, { params }),
  
  getAvailableQuizzes: (params?: any) => 
    api.get('/api/reports/quizzes/available', { params }),

  // Export functionality
  exportReport: (reportType: string, quizId?: number, params?: any) => 
    api.post('/api/reports/export', { reportType, quizId }, { params })
};